import {
  DAYS_PER_CHILD_AGE_UNIT,
  type ChildAgeUnit,
} from "@platform/formatting-utils";

import {
  type DraftDollarAmount,
  makeDraftDollarAmount,
  toDraftDollarAmounts,
} from "./DollarAmountsField";

/** Age units the child age-band editor offers per bound. */
export type AgeUnit = ChildAgeUnit;

// Bands store the unit the analyst chose (a single `unit` per band). The
// day-per-unit table — shared with the read/MP formatters so they never drift —
// is used to compare bounds across units and to collapse a mixed-unit band onto
// a single stored unit on save.
const DAYS_PER_UNIT = DAYS_PER_CHILD_AGE_UNIT;

/** Frontend-only benefit type whose amount is a list rather than a single value. */
export const MULTIPLE_DOLLAR_AMOUNTS = "multipleDollarAmounts";

/** Benefit type that carries no amount. */
export const NOT_INCLUDED = "notIncluded";

// The frontend invents `multipleDollarAmounts` so the editor knows to collect a
// list; the API enum has no such value, only `multipleOptions`. Every other
// caller of this array (the fixed BTX benefit-description fields) converts at
// the transform layer — this editor must do the same or the invented value
// reaches the database verbatim.
const API_MULTIPLE_OPTIONS = "multipleOptions";

/** Draft (frontend) benefit type → the API's enum value. */
export const toApiBenefitType = (frontendType: string): string =>
  frontendType === MULTIPLE_DOLLAR_AMOUNTS
    ? API_MULTIPLE_OPTIONS
    : frontendType;

/** API benefit type → the draft (frontend) value the editor understands. */
export const toFrontendBenefitType = (apiType: string): string =>
  apiType === API_MULTIPLE_OPTIONS ? MULTIPLE_DOLLAR_AMOUNTS : apiType;

/**
 * A single stored child age band — the API's `benefitAmountsByAge` element:
 * both bounds are expressed in `unit`, plus a typed benefit amount.
 */
export interface ChildAgeBand {
  ageBandLower?: number;
  ageBandUpper?: number;
  unit?: string;
  benefitAmount?: {
    type?: string;
    amounts?: number[];
  };
}

/** The draft (string-backed) form of a band while it is being edited. */
export interface DraftChildBand {
  /** Stable key for React, since bands can be added and removed. */
  id: string;
  lowerUnit: AgeUnit;
  lowerLimit: string;
  upperUnit: AgeUnit;
  upperLimit: string;
  benefitType: string;
  /** Single amount for the scalar types (dollar / percentage / increments). */
  amount: string;
  /** Amount list for the multiple-dollar-amounts type. */
  amounts: DraftDollarAmount[];
}

const asAgeUnit = (unit: string | undefined): AgeUnit =>
  unit === "months" || unit === "years" ? unit : "days";

/** Convert a value expressed in `unit` to whole days (days are integer-only). */
export const toDays = (unit: AgeUnit, value: number): number =>
  Math.round(value * DAYS_PER_UNIT[unit]);

/** Format a converted bound for a text input: integers plain, else trimmed. */
const toInput = (value: number): string =>
  Number.isInteger(value) ? String(value) : String(Number(value.toFixed(4)));

/** Ranking of units by fineness; a finer unit divides a coarser one evenly. */
const UNIT_FINENESS: Record<AgeUnit, number> = { days: 0, months: 1, years: 2 };

/** The finer (smaller) of two units — both bounds convert into it without a fraction. */
const finerUnit = (a: AgeUnit, b: AgeUnit): AgeUnit =>
  UNIT_FINENESS[a] <= UNIT_FINENESS[b] ? a : b;

/** Re-express `value` (in `from`) in `to`; a whole number when `to` is `from` or finer. */
const inUnit = (value: number, from: AgeUnit, to: AgeUnit): number =>
  Math.round((value * DAYS_PER_UNIT[from]) / DAYS_PER_UNIT[to]);

export const makeEmptyDraftChildBand = (): DraftChildBand => ({
  id: crypto.randomUUID(),
  lowerUnit: "days",
  lowerLimit: "",
  upperUnit: "days",
  upperLimit: "",
  benefitType: "",
  amount: "",
  amounts: [makeDraftDollarAmount()],
});

const toDraftChildBand = (band: ChildAgeBand): DraftChildBand => {
  const unit = asAgeUnit(band.unit);
  const type = toFrontendBenefitType(band.benefitAmount?.type ?? "");
  const amounts = band.benefitAmount?.amounts ?? [];
  const isSingle = type !== MULTIPLE_DOLLAR_AMOUNTS;

  // Both bounds are shown in the band's stored unit, values verbatim — no
  // re-derivation, so what was saved is what reopens.
  return {
    id: crypto.randomUUID(),
    lowerUnit: unit,
    lowerLimit: band.ageBandLower == null ? "" : String(band.ageBandLower),
    upperUnit: unit,
    upperLimit: band.ageBandUpper == null ? "" : String(band.ageBandUpper),
    benefitType: type,
    amount: isSingle && amounts.length > 0 ? toInput(amounts[0]) : "",
    amounts: toDraftDollarAmounts(amounts.map((amount) => ({ amount }))),
  };
};

/** A stored array becomes draft bands; an empty array seeds one blank band. */
export const toDraftChildBands = (bands: ChildAgeBand[]): DraftChildBand[] =>
  bands.length > 0 ? bands.map(toDraftChildBand) : [makeEmptyDraftChildBand()];

const boundsValid = (band: DraftChildBand): boolean => {
  const lower = Number(band.lowerLimit);
  const upper = Number(band.upperLimit);

  return (
    band.lowerLimit !== "" &&
    band.upperLimit !== "" &&
    Number.isFinite(lower) &&
    Number.isFinite(upper) &&
    lower >= 0 &&
    upper >= 0 &&
    toDays(band.lowerUnit, lower) <= toDays(band.upperUnit, upper)
  );
};

const amountValid = (band: DraftChildBand): boolean => {
  if (band.benefitType === NOT_INCLUDED) {
    return true;
  }

  if (band.benefitType === MULTIPLE_DOLLAR_AMOUNTS) {
    return band.amounts.some((entry) => Number(entry.amount) > 0);
  }

  return band.amount !== "" && Number(band.amount) >= 0;
};

export const isValidChildBand = (band: DraftChildBand): boolean =>
  band.benefitType !== "" && boundsValid(band) && amountValid(band);

/**
 * A band the user has not begun filling in. Blank bands are dropped on save, so
 * an untouched seed band leaves the field unset (0 bands ⇒ no data) rather than
 * failing validation.
 */
export const isBlankChildBand = (band: DraftChildBand): boolean =>
  band.lowerLimit === "" &&
  band.upperLimit === "" &&
  band.benefitType === "" &&
  band.amount === "";

const toAmounts = (band: DraftChildBand): number[] => {
  if (band.benefitType === NOT_INCLUDED) {
    return [];
  }

  if (band.benefitType === MULTIPLE_DOLLAR_AMOUNTS) {
    return band.amounts
      .map((entry) => Number(entry.amount))
      .filter((amount) => Number.isFinite(amount));
  }

  return band.amount === "" ? [] : [Number(band.amount)];
};

/**
 * Convert a draft band to the stored shape. A same-unit band stores that unit
 * and its values verbatim; a mixed-unit band can't fit two units in the single
 * `unit` field, so it stores the finer unit with both bounds expressed in it.
 */
export const toChildAgeBand = (band: DraftChildBand): ChildAgeBand => {
  const amounts = toAmounts(band);
  const unit = finerUnit(band.lowerUnit, band.upperUnit);

  return {
    ageBandLower: inUnit(Number(band.lowerLimit), band.lowerUnit, unit),
    ageBandUpper: inUnit(Number(band.upperLimit), band.upperUnit, unit),
    unit,
    benefitAmount: {
      type: toApiBenefitType(band.benefitType),
      ...(amounts.length > 0 ? { amounts } : {}),
    },
  };
};
